import React from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { observer } from 'mobx-react-lite';
import { useModal } from 'react-native-modalfy';
import { Platform } from 'react-native';

import { screens } from './screens';
import { TabNavigator } from './TabNavigator';
import type { MainStackParamListType } from './types';
import { PersonalizationNavigator } from './PersonalizationNavigator';
import { ProfileNavigator } from './ProfileNavigator';
import { HubNavigator } from './HubNavigator';
import { PostNavigator } from './PostNavigator';
import { ChatNavigator } from './ChatNavigator';
import { modals, ModalStackParamsType } from '@modals';
import { IS_IOS } from '@utils';
import {
  useAsyncInit,
  useDeepLinking,
  useNotificationsTokenListener,
  useMainSocketListeners,
} from '@hooks';
import { useAppModel } from '@models/App/AppModel';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { NotificationsService } from '@services';
import { SocketApi } from '@api';
import { useChatsModel } from '@models/Chats/ChatsModel';
import { SearchTabNavigator } from './SearchTabNavigator';
import { Header, WebHeader } from '@components';
import {
  CommentScreen,
  FollowersScreen,
  FollowingScreen,
  GalleryScreen,
  LikesScreen,
  ReportScreen,
  UserProfileScreen,
} from '@screens';
import { FeedbackNavigator } from './FeedbackNavigator';
import { useAuthModel } from '@models/AuthModel';

const MainStack =
  createNativeStackNavigator<MainStackParamListType>();

export const MainNavigator = observer(() => {
  const { openModal } = useModal<ModalStackParamsType>();
  const deepLinking = useDeepLinking();
  const app = useAppModel();
  const auth = useAuthModel();
  const viewer = useViewerModel();
  const chats = useChatsModel();

  useAsyncInit(async () => {
    deepLinking.updateHasEverBeenAuthorized();

    // Init SignalR WebSocket connection
    await SocketApi.connect();

    // Request iOS push notifications permission
    if (
      IS_IOS &&
      !app.wasNotificationPermissionEverRequested &&
      !(await NotificationsService.isPermissionStatusDetermined())
    ) {
      openModal(modals.AllowNotificationsModal, {
        onAllow: NotificationsService.requestPermission,
      });
      app.updateNotificationPermissionEverRequested();
    }
  });

  useMainSocketListeners({
    onConversationCount: ({ count }) => {
      chats.setNewCount(count);
    },
    onNewMessagesCount: ({ conversationId, ...params }) => {
      const conversation = chats.getById(conversationId);
      conversation?.setNewDetails(params);
    },
  });

  useNotificationsTokenListener(async (token) => {
    try {
      await viewer.addPushNotificationsToken.run({ token });
    } catch (error) {
      // Do nothing
    }
  });

  const shouldUsePersonalization =
    auth.isFirstLogin ||
    !!deepLinking.getInitialAction() ||
    viewer.currentUser?.settings.isNeedToPersonalize;

  const initialRouteName = shouldUsePersonalization
    ? screens.PersonalizationNavigator
    : screens.TabNavigator;

  // Prevent access to the rest of the app when subscription plan is completely inactive.
  // Also, check for current user to be defined to get proper tariff plan status,
  // otherwise Subscription screen will be always rendered initially.
  if (viewer.currentUser && !viewer.currentUser.tariffPlan.isActive) {
    return (
      <MainStack.Navigator
        initialRouteName={screens.ProfileNavigator}
        screenOptions={{
          headerShown: false,
        }}
      >
        <MainStack.Screen
          component={ProfileNavigator}
          name={screens.ProfileNavigator}
        />
      </MainStack.Navigator>
    );
  }

  return (
    <MainStack.Navigator
      initialRouteName={initialRouteName}
      screenOptions={{
        headerShown: false,
      }}
    >
      <MainStack.Screen
        component={TabNavigator}
        name={screens.TabNavigator}
      />
      <MainStack.Screen
        name={screens.SearchTabNavigator}
        component={SearchTabNavigator}
        options={{
          headerShown: true,
          header: (props) =>
            Platform.select({
              web: <WebHeader activeTabButtonName={screens.Search} />,
              default: <Header {...props} />,
            }),
          animation: 'fade',
        }}
      />
      <MainStack.Screen
        component={PersonalizationNavigator}
        name={screens.PersonalizationNavigator}
      />
      <MainStack.Screen
        component={ProfileNavigator}
        name={screens.ProfileNavigator}
      />
      <MainStack.Screen
        component={HubNavigator}
        name={screens.HubNavigator}
      />
      <MainStack.Screen
        component={FeedbackNavigator}
        name={screens.FeedbackNavigator}
      />
      <MainStack.Screen
        component={ChatNavigator}
        name={screens.ChatNavigator}
      />
      <MainStack.Screen
        component={PostNavigator}
        name={screens.PostNavigator}
      />
      <MainStack.Group
        screenOptions={{
          headerShown: true,
          header: (props) =>
            Platform.select({
              web: (
                <WebHeader
                  activeTabButtonName={screens.HomeTabNavigator}
                />
              ),
              default: <Header {...props} />,
            }),
        }}
      >
        <MainStack.Screen
          component={GalleryScreen}
          name={screens.Gallery}
        />
        <MainStack.Screen
          name={screens.Report}
          component={ReportScreen}
        />
        <MainStack.Screen
          name={screens.Comment}
          component={CommentScreen}
        />
        <MainStack.Screen
          name={screens.Likes}
          component={LikesScreen}
        />
        <MainStack.Screen
          name={screens.UserProfile}
          component={UserProfileScreen}
        />
        <MainStack.Screen
          name={screens.Followers}
          component={FollowersScreen}
        />
        <MainStack.Screen
          name={screens.Following}
          component={FollowingScreen}
        />
      </MainStack.Group>
    </MainStack.Navigator>
  );
});
